import type {ReactNode} from "react";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {
    RowEngagementPanelType,
    RowEngagementType,
    type CurrentlyOpenRowEngagementPanel,
} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import type {AsidePanelState} from "~/contexts/asidePanelContext";
import type {ResolvedCalendarScheduleEmptyState} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export enum ViewType {
    list = "0f9e8d7c-6b5a-4a39-8877-665544332211",
    calendar = "c2d49653-1a2b-4e5f-8a9b-0c1d2e3f4a5b",
    cards = "a66b87cb-29b7-4bdf-8379-c8317759a03e",
    graph = "9828ec85-6e29-4b71-b6c3-5efd4a56bdca",
    kanbanBoard = "ddc47753-0540-421d-969d-f22b31dce3b2",
    table = "4561552f-775c-4f20-af43-cbfcfc54ea96",
    focus = "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
}
/** Kanban column styling reused by list-view status lozenges (`listStatusConfig`). */
export type KanbanBoardColumnConfig = {
    id: Uuid;
    displayName: string;
    background: string;
    textColor: string;
    possibleValues: Array<string | number | boolean | Uuid>;
    headerBorderRadius?: string;
    headerBorderColor?: string;
    headerBorderWidth?: string;
};

/** List card density: full desktop row, compact row when a side panel narrows the list, or mobile grid. */
export type OttopilotListCardLayout = "desktop" | "compact" | "mobile";

/** Side-mounted panels (read / update / create) that shrink the list content width. */
export function isOttopilotListNarrowingPanelOpen<T extends {id: Uuid}>(
    panel: CurrentlyOpenRowEngagementPanel<T>,
    asidePanelState?: AsidePanelState | null,
): boolean {
    const isEngagementPanelNarrowing =
        panel.rowEngagementType !== RowEngagementType.none
        && panel.rowEngagementPanelType !== RowEngagementPanelType.fullscreen
        && (panel.rowEngagementPanelType === RowEngagementPanelType.sidebar
            || panel.rowEngagementPanelType === RowEngagementPanelType.contentArea);

    const isAsidePanelNarrowing =
        asidePanelState?.isOpen === true && asidePanelState.isFullscreen !== true;

    return isEngagementPanelNarrowing || isAsidePanelNarrowing;
}

/**
 * Below this content width, list cards use the stacked mobile layout even on a
 * wide viewport (e.g. profile Events column with summary + Notes aside open).
 */
export const LIST_CARD_NARROW_CONTAINER_MAX_WIDTH_PX = 640;

export function resolveOttopilotListCardLayout<T extends {id: Uuid}>(
    isMobileViewport: boolean,
    panel: CurrentlyOpenRowEngagementPanel<T>,
    asidePanelState?: AsidePanelState | null,
    isNarrowContainer = false,
): OttopilotListCardLayout {
    if (isMobileViewport === true || isNarrowContainer === true) {
        return "mobile";
    }
    if (isOttopilotListNarrowingPanelOpen(panel, asidePanelState) === true) {
        return "compact";
    }
    return "desktop";
}

/** Passed from {@link ListView} into list `cardRenderer` so cards can open the same read panel as the table view. */
export type OttopilotListCardRenderContext = {
    openReadPanel: () => void;
    layout?: OttopilotListCardLayout;
    selection?: {
        isSelected: boolean;
        onToggleSelect: () => void;
    };
};

export type OttopilotView<T extends {id: Uuid}> = {
    id: Uuid;
} & (
    | {
          viewType: ViewType.list;
          cardRenderer: (row: T, context?: OttopilotListCardRenderContext) => ReactNode;
          /** When set, replaces the default list card click (open read panel). */
          onListCardClick?: (row: T) => void;
      }
    | {
          viewType: ViewType.cards;
          cardRenderer: (row: T) => ReactNode;
      }
    | {
          viewType: ViewType.graph;
      }
    | {
          viewType: ViewType.kanbanBoard;
          filterFieldId: Uuid | string; // column id or name on which data is to be grouped
          columnConfig: Array<KanbanBoardColumnConfig>;
          cardRenderer: (row: T) => ReactNode;
          /** When set, replaces the default kanban card click (open read panel). */
          onKanbanCardClick?: (row: T) => void;
          /** Optional layout overrides (e.g. Amaron disposition tracker). */
          kanbanLayout?: {
              columnMinWidthPx?: number;
              columnGapPx?: number;
              /** Overrides the in-column card grid on mobile viewports (< 768px). */
              mobileCardsGridTemplateColumns?: string;
              /** Hides the column-header pin control (e.g. Amaron InQueue assigned kanban). */
              hideColumnPin?: boolean;
          };
      }
    | {
          viewType: ViewType.table;
          //   visibleColumns: Array<Uuid>;
          visibleColumns: Array<string>;
          otherArgs?: Record<string, unknown>;
      }
    | {
          viewType: ViewType.calendar;
          /** Optional. Extracts a type string from each row for calendar event coloring. */
          getEventType?: (row: T) => string | undefined;
          /** Optional. Maps event type key/uuid to human readable legend label. */
          getEventTypeLabel?: (type: string) => string;
          /** Optional. Maps each row to a template label for schedule card chip. */
          getEventTemplateLabel?: (row: T) => string | undefined;
          /** Optional. Maps each row to template chip stroke color. */
          getEventTemplateStrokeColor?: (row: T) => string | undefined;
          /** Optional. Create action used by calendar schedule empty states. */
          onCreateEventClick?: () => void;
          /** Optional. Copy for the schedule list empty state. */
          scheduleEmptyState?: ResolvedCalendarScheduleEmptyState;
          /** Optional. Static legend items (used when legend should not depend on visible events). */
          legendItems?: Array<{
              type: string;
              label: string;
              description?: string;
          }>;
          /** Optional. Maps event type to backgroundColor/borderColor/textColor. Used when getEventType is provided. */
          getEventColor?: (type: string | undefined) => {
              backgroundColor?: string;
              borderColor?: string;
              textColor?: string;
          };
          /** Optional. Controls all-day tile color behavior in calendar cards/modals. */
          allDayStyle?: {
              mode: "fixedGreen" | "inheritStatus";
          };
          /** Optional. When true, schedule list shows only start time instead of start–end range. */
          startTimeOnly?: boolean;
          /**
           * When true, time-grid slot labels and inline event times use 24-hour clock (e.g. 16:00),
           * avoiding ambiguous 12-hour labels without AM/PM.
           */
          use24HourTimeAxis?: boolean;
          /**
           * When true, the schedule empty-state "Create event" control is disabled while the visible
           * calendar period starts in a year after the current local calendar year.
           */
          disableCreateEventWhenViewYearAfterCurrent?: boolean;
          /**
           * When true, the schedule empty-state "Create event" button is disabled and shown with disabled styling.
           */
          disableCreateEventButton?: boolean;
          /**
           * When set, replaces the default calendar click behavior (open read panel).
           * Call `openReadPanel()` after any confirmation UI to open the sidebar view panel.
           */
          onCalendarEventClick?: (args: {row: T; openReadPanel: () => void}) => void;
          /**
           * When true, clicking a calendar event (month/week/day/schedule list) opens the day summary modal
           * for that date instead of opening the read panel immediately.
           */
          eventOpensDaySummaryModal?: boolean;
          /** Optional styling/content for the day summary modal (e.g. Emotorad My Sales Team). */
          calendarDaySummaryModalPresentation?: {
              kind: "emotoradMySalesTeam";
              entitySingularNoun: string;
              entityPluralNoun: string;
              getSecondaryLine: (row: T) => string;
          };
      }
    | {
          viewType: ViewType.focus;
          focusRenderer: (controller: OttopilotDataViewerController<T>) => ReactNode;
      }
);

/** Table → Kanban → List → Calendar, then other view types. Only configured views appear in the picker. */
const VIEW_DISPLAY_ORDER: Partial<Record<ViewType, number>> = {
    [ViewType.focus]: 0,
    [ViewType.table]: 1,
    [ViewType.kanbanBoard]: 2,
    [ViewType.list]: 3,
    [ViewType.calendar]: 4,
    [ViewType.cards]: 5,
    [ViewType.graph]: 6,
};

export function sortOttopilotViewsForDisplayOrder<T extends {id: Uuid}>(
    views: Array<OttopilotView<T>>,
): Array<OttopilotView<T>> {
    return [...views].sort((a, b) => {
        const orderA = VIEW_DISPLAY_ORDER[a.viewType];
        const orderB = VIEW_DISPLAY_ORDER[b.viewType];
        const indexA = orderA === undefined ? 999 : orderA;
        const indexB = orderB === undefined ? 999 : orderB;
        return indexA - indexB;
    });
}
